// @atlas/contracts — a felhasználói beállítások (`/preferences/*`) Zod-sémái: a kezdőlap elrendezése és a személyes
// jegyzet. A tároló az Odin `/iam/me/preferences/:key` (opak, app-izolált, OCC-s dokumentum); az értéket az Atlas BFF
// validálja ezekkel a sémákkal.
//
// A fájl CSAK sémákat tart — a ROUTE-ok az `index.ts` `preferences` sub-routerében élnek.
import { DASHBOARD_CATALOG_REV, DASHBOARD_MAX_TILES, DASHBOARD_WINDOW_DAYS, dashboardLayoutIssues, } from '@atlas/shared';
import { z } from 'zod';
/** A jegyzet szöveg-plafonja (karakter). */
export const NOTES_MAX_LENGTH = 4000;
const WindowDaysSchema = z.union([
    z.literal(DASHBOARD_WINDOW_DAYS[0]),
    z.literal(DASHBOARD_WINDOW_DAYS[1]),
    z.literal(DASHBOARD_WINDOW_DAYS[2]),
]);
/**
 * A kezdőlap-elrendezés OLVASOTT alakja: szerkezeti, katalógus-ellenőrzés NÉLKÜL. Egy kivezetett csempe id-je a
 * mentett értékben így sem töri az olvasást (a feloldás — `resolveLayout` — csendben eldobja).
 */
export const DashboardLayoutShapeSchema = z.object({
    v: z.literal(1),
    catalogRev: z.number().int().min(0),
    tiles: z
        .array(z.object({
        id: z.string().min(1).max(64),
        size: z.union([z.literal(1), z.literal(2)]),
        opts: z.object({ windowDays: WindowDaysSchema.optional() }).optional(),
    }))
        .max(DASHBOARD_MAX_TILES),
});
/**
 * A kezdőlap-elrendezés ÍRÁSI alakja: a szerkezet + a KATALÓGUS-ellenőrzés (`@atlas/shared`): ismert id, nincs
 * duplikátum, engedett méret, `catalogRev` ∈ [0, aktuális]. A JOGOT szándékosan nem nézi (l. `dashboard-tiles.ts`).
 */
export const DashboardLayoutSchema = DashboardLayoutShapeSchema.superRefine((layout, ctx) => {
    for (const issue of dashboardLayoutIssues(layout)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: issue.index === null ? ['catalogRev'] : ['tiles', issue.index],
            message: issue.code,
        });
    }
});
/** A személyes jegyzet (nincs rich text — nincs szanitálási felület). */
export const NotesPrefSchema = z.object({
    v: z.literal(1),
    text: z.string().max(NOTES_MAX_LENGTH),
});
/** A preferencia-dokumentum burkolója. `value: null` + `version: 0` = „sosem mentett" (a normál állapot, nem 404). */
export function preferenceEnvelope(value) {
    return z.object({
        value: value.nullable(),
        /** OCC: az `If-Match` fejlécben ezt kell visszaküldeni (új sorhoz `0`). */
        version: z.number().int().min(0),
        updatedAt: z.string().nullable(),
    });
}
export const DashboardLayoutEnvelopeSchema = preferenceEnvelope(DashboardLayoutShapeSchema);
export const NotesPrefEnvelopeSchema = preferenceEnvelope(NotesPrefSchema);
/** Az `If-Match` fejléc (a preferencia írásához/törléséhez KÖTELEZŐ — hiányában 428). */
export const PreferenceIfMatchHeaders = z.object({ 'if-match': z.string().optional() });
/** Az aktuális katalógus-revízió — a web ezzel ír (`catalogRev`). Újra-export: egy helyről olvasható a szerződésből. */
export const DASHBOARD_LAYOUT_CATALOG_REV = DASHBOARD_CATALOG_REV;
//# sourceMappingURL=schemas.js.map